import React from "react";
import { Pressable, Text, TextInput, View } from "react-native";

import { PrimaryButton } from "../../../../common/native";
import { styles } from "../HomeConversations.styles";
import { formatPhoneNumberDisplay } from "../../../../../utils/phoneNumber";

type MessageComposerProps = {
  fromNumbers: string[];
  fromE164: string;
  onFromE164Change: (fromE164: string) => void;
  toCountryCode?: string;
  toLocalNumber?: string;
  onToCountryCodeChange?: (value: string) => void;
  onToLocalNumberChange?: (value: string) => void;
  showToField?: boolean;
  draftBody: string;
  onDraftBodyChange: (value: string) => void;
  onSend: () => void;
  submitting: boolean;
  disabled: boolean;
  error: string | null;
};

const MessageComposer: React.FC<MessageComposerProps> = ({
  fromNumbers,
  fromE164,
  onFromE164Change,
  toCountryCode = "",
  toLocalNumber = "",
  onToCountryCodeChange,
  onToLocalNumberChange,
  showToField = false,
  draftBody,
  onDraftBodyChange,
  onSend,
  submitting,
  disabled,
  error,
}) => {
  const canSend = !disabled && !submitting && !!draftBody.trim();
  const handleComposerKeyPress = React.useCallback(
    (event: any) => {
      if (!canSend) {
        return;
      }

      const nativeEvent = event?.nativeEvent || {};
      const key = nativeEvent.key;
      const metaKey = !!nativeEvent.metaKey;
      const ctrlKey = !!nativeEvent.ctrlKey;

      if (key !== "Enter" || (!metaKey && !ctrlKey)) {
        return;
      }

      event?.preventDefault?.();
      onSend();
    },
    [canSend, onSend],
  );

  return (
    <View style={styles.messageComposerContainer}>
      {showToField ? (
        <>
          <Text style={styles.inputLabel}>To number</Text>
          <View style={styles.toNumberRow}>
            <TextInput
              value={toCountryCode}
              onChangeText={onToCountryCodeChange}
              style={[styles.input, styles.toNumberCountryCodeInput]}
              placeholder="+1"
              editable={!submitting}
              keyboardType="phone-pad"
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={4}
              accessibilityLabel="To phone country code"
            />
            <TextInput
              value={toLocalNumber}
              onChangeText={onToLocalNumberChange}
              style={[styles.input, styles.toNumberLocalInput]}
              placeholder="Phone number"
              editable={!submitting}
              keyboardType="phone-pad"
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={15}
              accessibilityLabel="To phone local number"
            />
          </View>
        </>
      ) : null}

      <Text style={styles.inputLabel}>From number</Text>
      {!fromNumbers.length ? (
        <Text style={styles.emptyText}>
          Assign a phone number in Config to send messages.
        </Text>
      ) : (
        <View style={styles.messageFromNumberRow}>
          {fromNumbers.map((number) => {
            const isSelected = number === fromE164;
            const formattedNumber = formatPhoneNumberDisplay(number, {
              fallback: number,
            });
            return (
              <Pressable
                key={number}
                style={[
                  styles.inlineOptionButton,
                  styles.messageFromNumberButton,
                  isSelected && styles.inlineOptionButtonActive,
                ]}
                onPress={() => onFromE164Change(number)}
                accessibilityLabel={`Use ${formattedNumber} as from number`}
              >
                <Text
                  style={[
                    styles.inlineOptionText,
                    isSelected && styles.inlineOptionTextActive,
                  ]}
                >
                  {formattedNumber}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}

      <Text style={styles.inputLabel}>Reply</Text>
      <TextInput
        value={draftBody}
        onChangeText={onDraftBodyChange}
        style={[styles.input, styles.messageComposerInput]}
        placeholder="Type a message"
        editable={!submitting}
        multiline
        textAlignVertical="top"
        onKeyPress={handleComposerKeyPress}
      />

      {error ? <Text style={styles.inlineErrorText}>{error}</Text> : null}

      <PrimaryButton
        text={submitting ? "Sending..." : "Send Message"}
        onPress={onSend}
        disabled={!canSend}
        loading={submitting}
        fullWidth
      />
    </View>
  );
};

export default MessageComposer;
